import React, { useState, useEffect } from 'react';
import { Typography, Grid, Box } from '@mui/material';
import Content from '../components/Layout/Content';
import { DEFAULT_NULL_REPLACEMENT } from '../shared/constants';
import { FETCH_PROFILE_DETAILS_QUERY } from '../services/AccountDetailsQueries/AccountDetailsService';
import { useQuery } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { BreadcrumbComponent } from '../components/BreadcrumbComponent/BreadcrumbComponent';
import AvatarImg from '../components/Layout/AvatarImg';
import { SimpleBackdrop } from '../components/SimpleBackdropComponent/SimpleBackdrop';
import avataricon from '../assests/avataricon.svg';
import {
  ACCOUNT_DETAILS,
  ACCOUNT_DETAILS_BREADCRUMBDATA as breadcrumbData,
} from '../shared/Strings';

function ReadonlyField({ label, value }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginRight: '60px',
        marginBottom: '20px',
      }}>
      <Typography variant="body1" color="#828282">
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </div>
  );
}

export const AccountDetails = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [accountData, setAccountData] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    bloodGroup: '',
    userId: '',
    gender: '',
    dob: '',
    avatarUrl: '',
    emailAddress: '',
    phoneNumber: '',
    city: '',
    state: '',
    addressLine1: '',
    addressLine2: '',
    zipCode: '',
  });

  const accountDetails = useQuery(FETCH_PROFILE_DETAILS_QUERY, {
    onError: err => {
      enqueueSnackbar(ACCOUNT_DETAILS.ON_ERROR_TEXT.WENT_WRONG_TEXT, {
        variant: 'error',
      });
    },
  });

  const loading = accountDetails.loading;

  useEffect(() => {
    if (accountDetails.data) {
      let result = accountDetails?.data?.fetchProfileDetails;
      setAccountData({
        firstName: result.basicProfileDetails.firstName[0],
        lastName: result.basicProfileDetails.lastName,
        preferredName: result.basicProfileDetails.preferredName,
        gender: result.basicProfileDetails.gender,
        userId: result.basicProfileDetails.userId,
        dob: result.basicProfileDetails.dob,
        bloodGroup: result.basicProfileDetails.bloodGroup,
        avatarUrl: result.basicProfileDetails.avatarUrl,
        emailAddress: result.contactProfileDetails.emailIdList?.[0]?.emailAddress,
        phoneNumber:
          result.contactProfileDetails.phoneNumberList?.[0]?.phoneNumber,
        addressLine1: result.contactProfileDetails.addressList?.[0]?.addressLine1,
        addressLine2: result.contactProfileDetails.addressList?.[0]?.addressLine2,
        zipCode: result.contactProfileDetails.addressList?.[0]?.zipCode,
        state: result.contactProfileDetails.addressList?.[0]?.state,
        city: result.contactProfileDetails.addressList?.[0]?.city,
      });
    }
  }, [accountDetails.data]);

  const details = [
    {
      label: ACCOUNT_DETAILS.DETAILS.FULL_NAME,
      value:
        `${accountData.firstName} ${accountData.lastName}` ||
        DEFAULT_NULL_REPLACEMENT,
    },
    {
      label: ACCOUNT_DETAILS.DETAILS.PREFERRED_NAME,
      value: accountData.preferredName || DEFAULT_NULL_REPLACEMENT,
    },
    {
      label: ACCOUNT_DETAILS.DETAILS.GENDER,
      value: accountData.gender || DEFAULT_NULL_REPLACEMENT,
    },
    {
      label: ACCOUNT_DETAILS.DETAILS.USER_ID,
      value: accountData.userId || DEFAULT_NULL_REPLACEMENT,
    },
    {
      label: ACCOUNT_DETAILS.DETAILS.DATE_OF_BIRTH,
      value:
        `${new Date(accountData.dob.slice(0, 10)).toLocaleDateString(
          'en-US'
        )}` || DEFAULT_NULL_REPLACEMENT,
    },
    {
      label: ACCOUNT_DETAILS.DETAILS.BLOOD_GROUP,
      value: accountData.bloodGroup || DEFAULT_NULL_REPLACEMENT,
    },
  ];

  const addressDetails = [
    {
      label: ACCOUNT_DETAILS.DETAILS.PRIMARY_EMAIL_ADDRESS,
      value: accountData?.emailAddress || DEFAULT_NULL_REPLACEMENT,
    },
    {
      label: ACCOUNT_DETAILS.DETAILS.PRIMARY_PHONE_NUMBER,
      value:
        `(${accountData.phoneNumber.slice(
          0,
          3
        )})-${accountData.phoneNumber.slice(
          3,
          6
        )}-${accountData.phoneNumber.slice(6)}` || DEFAULT_NULL_REPLACEMENT,
    },
    {
      label: ACCOUNT_DETAILS.DETAILS.PRIMARY_ADDRESS,
      value:
        `${accountData.addressLine1} ${accountData.addressLine2}, ${accountData.zipCode}, ${accountData.city}, ${accountData.state}` ||
        DEFAULT_NULL_REPLACEMENT,
    },
  ];

  return (
    <Content>
      <BreadcrumbComponent breadcrumbData={breadcrumbData} />
      <Typography variant="title">{ACCOUNT_DETAILS.TITLE_TEXT}</Typography>
      {!loading && (
        <>
          <Box
            sx={{
              maxWidth: '1056px',
              width: '100%',
              borderRadius: ' 8px',
              mb: '24px',
              mt: '32px',
              bgcolor: '#fff',
              p: '24px',
            }}>
            <Grid container>
              <Grid item sx={{ mb: '24px', mr: '24px' }}>
                <AvatarImg
                  sx={{
                    width: 104,
                    height: 104,
                    mr: 0,
                    mt: 0,
                    fontWeight: 500,
                  }}
                  src={accountData.avatarUrl || avataricon}
                  userName={accountData.firstName}
                />
              </Grid>
              <Grid item>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: '36px' }}>
                  {ACCOUNT_DETAILS.PERSONAL_DETAILS_TEXT}
                </Typography>
                <Grid container>
                  {details.map((item, index) => (
                    <Grid item key={index}>
                      <ReadonlyField label={item.label} value={item.value} />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              maxWidth: '1056px',
              width: '100%',
              borderRadius: '8px',
              bgcolor: '#fff',
            }}>
            <Grid
              container
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mt: '28px',
                p: '24px',
              }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: '50px' }}>
                {ACCOUNT_DETAILS.CONTACT_DETAILS_TEXT}
              </Typography>
              {addressDetails.map(item => (
                <Grid item key={item.label} sx={{ mb: '24px' }}>
                  <ReadonlyField label={item.label} value={item.value} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      )}
      <SimpleBackdrop open={loading} />
    </Content>
  );
};
